import { Card, Col, Row } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import SemiCircleChartSkeleton from '@/app/common/components/skeleton/SemiCircleChartSkeleton';
import { OverviewSectionTitle } from './OverviewSectionTitle';

const SkeletonLines = ({ count, width = 100 }: { count: number; width?: number }) => (
  <div className="d-flex flex-column gap-2">
    {Array.from({ length: count }, (_, index) => (
      <TextLoading key={index} width={width} height={10} />
    ))}
  </div>
);

const GaugeCardSkeleton = () => (
  <Card className="h-100">
    <FalconCardHeader
      title={<TextLoading width={40} height={12} />}
      titleTag="h6"
      className="py-2"
      light
    />
    <Card.Body>
      <SemiCircleChartSkeleton title="" tooltip="" minHeight="180px" />
      <div className="d-flex flex-column align-items-center gap-2 mt-2">
        <TextLoading width={30} height={12} />
        <TextLoading width={20} height={10} />
      </div>
      <Row className="g-2 mt-3">
        <Col xs={6}>
          <TextLoading height={28} />
        </Col>
        <Col xs={6}>
          <TextLoading height={28} />
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const ListCardSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <Card className="h-100">
    <FalconCardHeader
      title={<TextLoading width={45} height={12} />}
      titleTag="h6"
      className="py-2"
      light
    />
    <Card.Body>
      <div className="d-flex flex-column gap-2">
        {Array.from({ length: rows }, (_, index) => (
          <TextLoading key={index} height={36} />
        ))}
      </div>
    </Card.Body>
  </Card>
);

export const AiAnalysisOverviewSkeleton = () => (
  <>
    <OverviewSectionTitle
      icon="search"
      title="تحلیل سئو"
      subtitle="وضعیت سئوی محتوایی و فنی صفحه در یک نگاه"
      helpHtml="جزئیات امتیاز سئو، معیارهای محتوایی و فنی صفحه و یافته‌های سلامت سئو."
      className="mb-3 mt-4"
    />
    <Row className="g-3 mb-4">
      {Array.from({ length: 4 }, (_, index) => (
        <Col key={index} xs={12} sm={6} lg={3}>
          <Card className="h-100">
            <Card.Body>
              <TextLoading width={50} height={12} />
              <div className="my-3">
                <TextLoading width={30} height={22} />
              </div>
              <TextLoading width={40} height={10} />
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
    <Row className="g-3 mb-4">
      <Col xs={12} lg={4}>
        <GaugeCardSkeleton />
      </Col>
      <Col xs={12} lg={4}>
        <ListCardSkeleton rows={6} />
      </Col>
      <Col xs={12} lg={4}>
        <ListCardSkeleton rows={4} />
      </Col>
    </Row>
    <OverviewSectionTitle
      icon="robot"
      title="تشخیص AI"
      subtitle="میزان شباهت نوشتار به الگوی انسانی و ماشینی"
      helpHtml="این بخش الگوهای نوشتاری صفحه را با محتوای انسانی و تولیدشده توسط AI مقایسه می‌کند."
    />
    <Row className="g-3 mb-4">
      <Col xs={12} xl={4}>
        <GaugeCardSkeleton />
      </Col>
      <Col xs={12} md={6} xl={4}>
        <ListCardSkeleton rows={3} />
      </Col>
      <Col xs={12} md={6} xl={4}>
        <ListCardSkeleton rows={3} />
      </Col>
    </Row>
    <OverviewSectionTitle
      icon="award"
      title="تحلیل E-E-A-T"
      subtitle="تجربه، تخصص، اعتبار و اعتماد صفحه"
      helpHtml="ارزیابی کیفیت تجربه، تخصص، اعتبار و اعتماد صفحه بر اساس چارچوب E-E-A-T گوگل."
    />
    <Row className="g-3 mb-4">
      <Col xs={12} lg={4}>
        <GaugeCardSkeleton />
      </Col>
      <Col xs={12} lg={4}>
        <ListCardSkeleton rows={4} />
      </Col>
      <Col xs={12} lg={4}>
        <ListCardSkeleton rows={4} />
      </Col>
    </Row>
    <OverviewSectionTitle
      icon="globe"
      title="تحلیل GEO"
      subtitle="آمادگی صفحه برای جستجوی مولد و استنادپذیری"
      helpHtml="ارزیابی آمادگی صفحه برای نمایش یا استناد در جستجوی مبتنی بر هوش مصنوعی."
    />
    <Row className="g-3 mb-4">
      <Col xs={12} lg={4}>
        <GaugeCardSkeleton />
      </Col>
      <Col xs={12} lg={4}>
        <ListCardSkeleton rows={5} />
      </Col>
      <Col xs={12} lg={4}>
        <ListCardSkeleton rows={4} />
      </Col>
    </Row>
  </>
);

export const AiAnalysisReportSkeleton = () => (
  <Row className="g-3">
    <Col xs={12} lg={8} className="order-1 order-lg-0">
      <Card className="w-100">
        <FalconCardHeader
          title={
            <div className="d-flex align-items-center justify-content-between">
              <TextLoading width={28} height={12} />
              <TextLoading width={18} height={22} isWidthPx={false} />
            </div>
          }
          titleTag="h6"
          className="py-2"
          light
        />
        <Card.Body>
          <SkeletonLines count={12} />
        </Card.Body>
      </Card>
    </Col>
    <Col xs={12} lg={4} className="order-0 order-lg-1">
      <GaugeCardSkeleton />
    </Col>
  </Row>
);
